import React, { useContext, useEffect, useRef, useState } from "react";
import IGiftCardTemplate from "../../../../models/GiftCardTemplate.model";
import { CustomiseCardStepModel, ICustomiseCardStepModel } from "../../../../models/buy-giftcard/customise-card/CustomiseCardStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { IBuyGiftCardStepProps } from "../../../../models/interfaces/BuyGiftCardStep.interface";
import DataService from "../../../../services/DataService.service";
import { AppSettingContext } from "../../../Application";
import LoadingComponent from "../../../shared/LoadingComponent";
import MediaUploader from "../../shared/MediaUploader";

interface ICustomiseCardStepProps extends IBuyGiftCardStepProps {
    stepModel: ICustomiseCardStepModel;

    handleTemplateChange(model: ICustomiseCardStepModel): void;
    handleGoBack(): void;
    handleSubmit(stepModel: ICustomiseCardStepModel): void;
}

const CustomiseGiftCardStep = (props: ICustomiseCardStepProps) => {
    const { labels } = useContext(AppSettingContext);
    const checkboxRef = useRef(null);
    const culture = document.documentElement.lang;

    //#region STATE PROPERTIES

    const [stepModel, setStepModel] = useState<ICustomiseCardStepModel>({
        giftCardUrl: props.stepModel?.giftCardUrl ?? "",
        useCardTemplate: props.stepModel?.useCardTemplate,
        userGiftCards: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isModelValid, setIsModelValid] = useState<boolean>();
    const [templates, setTemplates] = useState(Array<IGiftCardTemplate>(0))
    const [lastUploadedTemplate, setLastUploadedTemplate] = useState("");

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        setIsLoading(true);
        loadAllTemplates();
    }, []);

    useEffect(() => {
        if (templates && templates.length > 0) {
            if (!templates.map(x => x.imageUrl).includes(stepModel.giftCardUrl)) {
                setLastUploadedTemplate(stepModel.giftCardUrl);
                handleUseCardTemplateChange(false);
            }

            setIsLoading(false);
        }
    }, [templates]);

    useEffect(() => {
        setIsModelValid(isValid());
    }, [stepModel]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    useEffect(() => {
        checkboxRef.current.checked = props.activeStep === BuyGiftCardStateEnum.CUSTOMISE_CARD;
    }, [props.activeStep]);

    //#endregion

    //#region HANDLERS

    const handleExpandBoxCheck = (): void => {
        if (checkboxRef.current.checked && props.activeStep !== BuyGiftCardStateEnum.CUSTOMISE_CARD) {
            props.handleSetActiveStep(BuyGiftCardStateEnum.CUSTOMISE_CARD);
        }
    };

    const handleUseCardTemplateChange = (useCardTemplate: boolean): void => {
        setStepModel({
            ...stepModel,
            useCardTemplate: useCardTemplate
        });
    };

    const handleTemplateChange = (templateUrl: string): void => {
        if (templateUrl.trim()) {
            setStepModel({
                ...stepModel,
                giftCardUrl: templateUrl
            });

            props.handleTemplateChange({
                giftCardUrl: templateUrl,
                useCardTemplate: stepModel.useCardTemplate,
                userGiftCards: stepModel.userGiftCards
            });
        }
    };

    const handleGoBack = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();
        props.handleGoBack();
    };

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        if (!isValid()) {
            return;
        }

        props.handleSubmit(stepModel);
        checkboxRef.current.checked = false;
    };

    //#endregion

    //#region COMMON HELPERS

    const loadAllTemplates = (): void => {
        DataService.getGiftCardsTemplates(culture)
            .then(response => {
                if (response) {
                    const resTemplates = Array<IGiftCardTemplate>(0);

                    response.forEach(item => {
                        resTemplates.push(item);
                    });

                    setTemplates(resTemplates);
                }
            }).catch(error => {
                console.error(error);
            });
    };

    const isValid = (): boolean => {
        return CustomiseCardStepModel.isValid(stepModel);
    };

    //#endregion

    return (
        <div className="card-customization box">
            <input
                type="checkbox"
                name="card-customization"
                id="card-customization"
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={true}
            />
            <div className="action-card">
                <h4 className="box-heading">
                    <i className="box-icon">
                        <img src="/static/options-card-page/options-card-icon-06.png" alt="icon 06" />
                    </i>
                    {labels.get("GiftCard.App.V2.CustomiseCard.Title")}
                    <i
                        className="box-arrow"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            checkboxRef.current.checked = !checkboxRef.current.checked;
                            handleExpandBoxCheck();
                        }}
                    ></i>
                </h4>

                {props.activeStep === BuyGiftCardStateEnum.CUSTOMISE_CARD && (
                    <div className="show">
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div className="slide-content">
                                <ul className="card-cover">
                                    <li>
                                        <input
                                            type="radio"
                                            name="card-cover"
                                            id="card-template"
                                            checked={stepModel.useCardTemplate}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    handleUseCardTemplateChange(true);
                                                }
                                            }}
                                        />
                                        <label htmlFor="card-cover">
                                            {labels.get("GiftCard.App.V2.CustomiseCard.Button.CardTemplate")}
                                        </label>
                                    </li>
                                    <li>
                                        <input
                                            type="radio"
                                            name="card-cover"
                                            id="upload-photo"
                                            checked={!stepModel.useCardTemplate}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    handleUseCardTemplateChange(false);
                                                }
                                            }}
                                        />
                                        <label htmlFor="upload-photo">
                                            {labels.get("GiftCard.App.V2.CustomiseCard.Button.Upload")}
                                        </label>
                                    </li>
                                </ul>

                                {stepModel.useCardTemplate ? (
                                    <div className="card-templates fadeIn">
                                        {templates.map((template, index) => (
                                            <img
                                                key={`template_${index}`}
                                                alt="Template Image"
                                                className={template.imageUrl === stepModel.giftCardUrl ? "selected" : ""}
                                                src={template.imageUrl}
                                                onClick={() => handleTemplateChange(template.imageUrl)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <MediaUploader
                                        uploaderId={1}
                                        selectedMediaUrl={stepModel.giftCardUrl}
                                        lastUploadedTemplate={lastUploadedTemplate}
                                        isVideoAllowed={false}
                                        imageCropperAspectRatio={1.59}

                                        setLastUploadedTemplate={setLastUploadedTemplate}
                                        handleMediaChange={handleTemplateChange}
                                    />
                                )}

                                <div className="buttons">
                                    <button
                                        className="btn btn__full_width btn__secondary large-fs btn__large"
                                        onClick={handleGoBack}
                                    >
                                        {labels.get("GiftCard.App.V2.CustomiseCard.Button.GoBack")}
                                    </button>
                                    <button
                                        className="btn btn__full_width btn__primary large-fs btn__large"
                                        onClick={handleSaveAndContinue}
                                        disabled={!isModelValid}
                                    >
                                        {labels.get("GiftCard.App.V2.CustomiseCard.Button.Submit")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomiseGiftCardStep;